import { motion } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  Activity,
  Clock,
  Coins,
  Gem,
} from "lucide-react";

const items = [
  {
    title: "Custom items & worlds",
    desc:
      "Exclusive blocks, seeds and worlds you won't find anywhere else. Crafted by the OriginPS team.",
    icon: Sparkles,
    span: "md:col-span-2",
    accent: "from-amber-200/30 to-amber-500/0",
    testid: "feature-custom-items",
  },
  {
    title: "24/7 uptime",
    desc: "Stable, monitored infrastructure with auto-restart and backups.",
    icon: Clock,
    accent: "from-emerald-200/25 to-emerald-500/0",
    testid: "feature-uptime",
  },
  {
    title: "Active community",
    desc: "Daily events, giveaways and a thriving Discord.",
    icon: Activity,
    accent: "from-blue-200/25 to-blue-500/0",
    testid: "feature-community",
  },
  {
    title: "Fair economy",
    desc: "Carefully balanced drops & gem economy that rewards play.",
    icon: Coins,
    accent: "from-amber-200/25 to-amber-500/0",
    testid: "feature-economy",
  },
  {
    title: "Anti-cheat & safety",
    desc: "Active staff and protective tools keep the game clean.",
    icon: ShieldCheck,
    accent: "from-rose-200/25 to-rose-500/0",
    testid: "feature-anticheat",
  },
  {
    title: "Premium polish",
    desc:
      "Smooth gameplay, low latency and quality of life features baked in.",
    icon: Gem,
    span: "md:col-span-2",
    accent: "from-indigo-200/25 to-indigo-500/0",
    testid: "feature-polish",
  },
];

function FeatureCard({ item, index }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.7,
        delay: 0.05 * index,
        ease: [0.22, 1, 0.36, 1],
      }}
      data-testid={item.testid}
      className={`relative glass-card ring-border p-6 sm:p-7 ${item.span || ""} group hover:-translate-y-0.5 transition-transform duration-500`}
    >
      <div
        className={`pointer-events-none absolute -inset-px rounded-[inherit] bg-gradient-to-br ${item.accent} opacity-0 group-hover:opacity-100 transition-opacity`}
      />
      <div className="relative flex items-start gap-4">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/[0.05] border border-white/10 shadow-inner">
          <Icon className="h-5 w-5 text-amber-300" />
        </span>
        <div className="flex-1">
          <h3 className="font-display text-lg sm:text-xl font-medium text-white">
            {item.title}
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
            {item.desc}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32"
    >
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300">
          <Sparkles className="h-3 w-3 text-amber-300" />
          Why OriginPS
        </div>
        <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
          Designed for players who expect more.
        </h2>
        <p className="mt-3 text-base sm:text-lg text-slate-400">
          A private server that feels premium — from the lobby to the last
          block. Everything is tuned so you can focus on building, trading and
          having fun.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it, i) => (
          <FeatureCard key={it.title} item={it} index={i} />
        ))}
      </div>
    </section>
  );
}
